import React from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import Reveal from './Reveal';
import { PALETTE, RADIUS } from '../theme';
import { VaqtlyMark } from './ProductLogos';
import { products } from '../data/products';
import { founders, initials, Member, team } from '../data/team';
import { useT } from '../lib/i18n';

/**
 * Avatar tints, cycled per person within a group.
 *
 * Each is a soft ground with its own darker ink, so initials stay readable — the
 * saturated Duolingo fills are 2–3:1 against white text and would not.
 */
const TINTS = [
  { bg: PALETTE.signalSoft, ink: PALETTE.signalDepth },
  { bg: PALETTE.greenSoft, ink: PALETTE.greenDepth },
  { bg: '#fff1cc', ink: '#8a5a00' },
  { bg: '#ffe4e4', ink: PALETTE.redDepth },
] as const;

/**
 * Team, grouped under the product each person works on.
 *
 * Grouped rather than one flat grid because that is the actual shape of the
 * company: Vaqtly is a holding, and a visitor reading a product tile above wants
 * to know who is behind *that* product, not the union of everyone.
 */
export default function TeamSection() {
  const t = useT();

  /*
    The holding's founders lead, then one group per product in the order the
    tiles above use — so the page reads company first, products second. A group
    with nobody in it is dropped rather than rendered as an empty heading.
  */
  const groups: Array<{ key: string; label: string; Mark: React.ComponentType<{ size?: number }>; members: Member[] }> =
    [
      { key: 'founders', label: t.team.founders, Mark: VaqtlyMark, members: founders },
      ...products.map((product) => ({
        key: product.id,
        label: product.name,
        Mark: product.Logo,
        members: team[product.id] ?? [],
      })),
    ].filter((group) => group.members.length > 0);

  if (groups.length === 0) return null;

  return (
    <Box id="team" sx={{ scrollMarginTop: 96, py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <Reveal>
          <Typography
            component="p"
            sx={{
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: PALETTE.signal,
            }}
          >
            {t.team.eyebrow}
          </Typography>
          <Typography
            variant="h2"
            sx={{ mt: 1.5, fontSize: { xs: 30, md: 42 }, fontWeight: 800, color: PALETTE.ink, maxWidth: 720 }}
          >
            {t.team.heading}
          </Typography>
          <Typography sx={{ mt: 2, maxWidth: 560, fontSize: { xs: 16, md: 18 }, color: PALETTE.inkMuted }}>
            {t.team.body}
          </Typography>
        </Reveal>

        <Stack spacing={{ xs: 5, md: 7 }} sx={{ mt: { xs: 5, md: 7 } }}>
          {groups.map(({ key, label, Mark, members }, groupIndex) => (
            <Box key={key}>
              {/* Each group is headed by its own mark — the Vaqtly squircle for the
                  founders, the product's for a product — so the tie to the tiles
                  above is visual and needs no explaining. */}
              <Reveal delay={groupIndex * 80}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                  <Box sx={{ borderRadius: '22%', overflow: 'hidden', boxShadow: '0 2px 0 0 rgba(0,0,0,0.10)' }}>
                    <Mark size={36} />
                  </Box>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: PALETTE.ink }}>{label}</Typography>
                </Stack>
              </Reveal>

              <Box
                sx={{
                  display: 'grid',
                  gap: 2.5,
                  /*
                    Two then four, skipping three. A team is four people, and a
                    three-wide grid breaks that into 3 + 1 — one card stranded on
                    its own row, which reads as a mistake rather than a layout.
                    Both counts here divide four evenly.
                  */
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                }}
              >
                {members.map((member, index) => (
                  <Reveal key={`${member.name}-${index}`} delay={index * 70} animation="duoPop">
                    <MemberCard member={member} tint={TINTS[index % TINTS.length]} />
                  </Reveal>
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}

function MemberCard({ member, tint }: { member: Member; tint: (typeof TINTS)[number] }) {
  const t = useT();

  return (
    <Box
      sx={{
        height: '100%',
        p: 2.5,
        textAlign: 'center',
        borderRadius: `${RADIUS.card}px`,
        border: `2px solid ${PALETTE.hairline}`,
        backgroundColor: PALETTE.ground,
        boxShadow: `0 4px 0 0 ${PALETTE.hairline}`,
        transition: 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
        '@media (hover: hover)': {
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 8px 0 0 ${PALETTE.hairline}`,
            borderColor: PALETTE.inkFaint,
          },
        },
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          mx: 'auto',
          mb: 1.75,
          borderRadius: '50%',
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: tint.bg,
          color: tint.ink,
          fontSize: 24,
          fontWeight: 800,
          letterSpacing: '0.02em',
        }}
      >
        {member.photo ? (
          <Box
            component="img"
            src={member.photo}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          /* Decorative: the name is right below in text, so a screen reader
             announcing the initials as well would just repeat it. */
          <Box component="span" aria-hidden="true">
            {initials(member.name)}
          </Box>
        )}
      </Box>

      <Typography sx={{ fontSize: 16, fontWeight: 800, color: PALETTE.ink, lineHeight: 1.3 }}>
        {member.name}
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: 14, fontWeight: 700, color: PALETTE.inkMuted }}>
        {t.roles[member.role]}
      </Typography>
    </Box>
  );
}
